import {
  Box,
  Button,
  Input,
  Select,
  HStack,
  Text,
  Switch,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import type { Property } from "../../../../types";
import {
  PropertiesContext,
  PropertiesContextType,
} from "../../../../contexts/PropertiesContext";
import { isUserAdmin } from "../../../../lib/supabase";

export default function PropertyFilters() {
  const { filters, setFilters } = useContext<PropertiesContextType | undefined>(
    PropertiesContext
  ) as PropertiesContextType;

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      const adminStatus = await isUserAdmin();
      setIsAdmin(adminStatus);
    };
    checkAdminStatus();
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilters({ ...filters, search: value, page: 1 });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFilters({
      ...filters,
      status: value === "all" ? undefined : (value as Property["status"]),
      page: 1,
    });
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const [sortBy, sortOrder] = value.split(":");
    setFilters({
      ...filters,
      sortBy,
      sortOrder: sortOrder as "asc" | "desc",
      page: 1,
    });
  };

  const handleShowDeletedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({
      ...filters,
      showDeleted: e.target.checked,
      page: 1,
    });
  };

  const handleReset = () => {
    setFilters({
      page: 1,
      limit: 12,
      search: "",
      sortBy: "created_at",
      sortOrder: "desc",
      showDeleted: false,
    });
  };

  const currentSort = `${filters.sortBy || "created_at"}:${
    filters.sortOrder || "desc"
  }`;

  return (
    <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
      {/* All filters in a single row */}
      <HStack spacing={3} align="center" w="100%">
        {/* Search Input - First item, takes remaining space */}
        <Input
          placeholder="Buscar por título ou código..."
          value={filters.search || ""}
          onChange={handleSearchChange}
          variant="filled"
          bg="white"
          _hover={{ bg: "white" }}
          _focus={{ bg: "white", borderColor: "blue.500" }}
          className="grow min-w-[200px] w-full"
        />

        {/* Status Filter */}
        <Select
          value={filters.status || "all"}
          onChange={handleStatusChange}
          variant="filled"
          bg="white"
          _hover={{ bg: "white" }}
          _focus={{ bg: "white", borderColor: "blue.500" }}
          size="sm"
          minWidth="140px"
          maxWidth="140px"
          flexShrink={0}
        >
          <option value="all">Todos os Status</option>
          <option value="active">Ativo</option>
          <option value="hidden">Oculto</option>
          <option value="inactive">Inativo</option>
          <option value="draft">Rascunho</option>
        </Select>

        {/* Sort Label and Select */}
        <Text fontSize="sm" color="gray.600" fontWeight="medium" flexShrink={0}>
          Ordenar:
        </Text>
        <Select
          value={currentSort}
          onChange={handleSortChange}
          variant="filled"
          bg="white"
          _hover={{ bg: "white" }}
          _focus={{ bg: "white", borderColor: "blue.500" }}
          size="sm"
          minWidth="160px"
          maxWidth="160px"
          flexShrink={0}
        >
          <option value="title:asc">Título (A-Z)</option>
          <option value="title:desc">Título (Z-A)</option>
          <option value="code:asc">Código (A-Z)</option>
          <option value="code:desc">Código (Z-A)</option>
          <option value="created_at:desc">Mais Recentes</option>
          <option value="created_at:asc">Mais Antigos</option>
          <option value="updated_at:desc">Atualizados</option>
        </Select>

        {/* Admin Toggle for Deleted Items */}
        {isAdmin && (
          <FormControl display="flex" alignItems="center" width="fit-content">
            <FormLabel
              htmlFor="show-deleted"
              mb="0"
              fontSize="sm"
              color="gray.600"
              whiteSpace="nowrap"
            >
              Excluídos:
            </FormLabel>
            <Switch
              id="show-deleted"
              isChecked={filters.showDeleted || false}
              onChange={handleShowDeletedChange}
              colorScheme="red"
              size="sm"
            />
          </FormControl>
        )}

        {/* Reset Button */}
        <Button
          onClick={handleReset}
          colorScheme="gray"
          size="sm"
          flexShrink={0}
        >
          Limpar
        </Button>
      </HStack>
    </Box>
  );
}
